import { z } from 'zod';

const findingSchema = z.object({
  title: z.string(),
  evidence: z.string().nullish(),
  context: z.string().nullish(),
});

const recommendationSchema = z.object({
  action: z.string(),
  rationale: z.string().nullish(),
  expected_impact: z.string().nullish(),
  priority: z.string().nullish(),
});

const reportSchema = z.object({
  summary: z.string().nullish(),
  key_findings: z.array(findingSchema).default([]),
  recommendations: z.array(recommendationSchema).default([]),
});

const scopeSchema = z.object({
  n_creatives_segment: z.number().nullish(),
  n_creatives_brand: z.number().nullish(),
  networks: z.array(z.string()).default([]),
});

const chartValueSchema = z.object({
  metric: z.string(),
  value: z.number().nullable(),
  format: z.string(),
  precision: z.number().nullish(),
  currency_symbol: z.string().nullish(),
});

const chartRowSchema = z.object({
  label: z.string(),
  detail: z.string().default(''),
  value: z.number(),
  is_improvement: z.boolean().nullish(),
});

// Mirrors AiInsightsChart (actions src/outputs.py): row values are fractions.
const chartSchema = z.object({
  key: z.string(),
  kind: z.enum(['bars', 'share', 'columns']),
  unit: z.enum(['change', 'share']),
  title: z.string(),
  subtitle: z.string(),
  rows: z.array(chartRowSchema).default([]),
  baseline: chartValueSchema.nullish(),
  stats: z.array(chartValueSchema).default([]),
  unavailable_reason: z.string().nullish(),
});

const reportResultSchema = z.object({
  report: reportSchema,
  scope: scopeSchema.nullish(),
  // Each chart parses alone, so one this build cannot draw drops out by itself.
  charts: z.array(z.unknown()).default([]),
});

export type Report = z.infer<typeof reportSchema>;
export type ReportScope = z.infer<typeof scopeSchema>;
export type Finding = z.infer<typeof findingSchema>;
export type Recommendation = z.infer<typeof recommendationSchema>;
export type InsightsChart = z.infer<typeof chartSchema>;
export type ChartValue = z.infer<typeof chartValueSchema>;

export interface ReportResult {
  report: Report;
  scope: ReportScope | null;
  charts: InsightsChart[];
}

export function parseReport(result: unknown): ReportResult | null {
  const parsed = reportResultSchema.safeParse(result);
  if (!parsed.success) return null;
  return {
    report: parsed.data.report,
    scope: parsed.data.scope ?? null,
    charts: parsed.data.charts.flatMap((chart) => {
      const read = chartSchema.safeParse(chart);
      return read.success ? [read.data] : [];
    }),
  };
}

/** The creatives and networks a report covers, as the panel and the download write them. */
export function scopeText(scope: ReportScope): string | null {
  const count = scope.n_creatives_segment;
  const parts = [
    count === null || count === undefined ? null : `${count} creative${count === 1 ? '' : 's'}`,
    scope.networks.length === 0 ? null : scope.networks.join(', '),
  ].filter((part) => part !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}
